import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiX } from 'react-icons/fi';
import { useApp } from '../context/AppContext';
import ProjectCard from '../components/ProjectCard';

const categories = ['All', 'Web Development', 'Web App Development', 'UI/UX Design', 'Branding', 'Software Development'];

const ProjectsPage = () => {
  const { projects } = useApp();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = useMemo(() => {
    return projects.filter(p => {
      const matchCat = activeCategory === 'All' || p.category === activeCategory;
      const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) ||
        p.description.toLowerCase().includes(search.toLowerCase()) ||
        p.technologies.some(t => t.toLowerCase().includes(search.toLowerCase()));
      return matchCat && matchSearch;
    });
  }, [projects, search, activeCategory]);

  return (
    <div className="min-h-screen pt-32 pb-24">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 mb-16">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-4">
          Portfolio
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="font-display text-7xl font-light text-white mb-6">
          Our <em className="accent-text not-italic">Projects</em>
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          className="font-body text-white/50 max-w-md">
          A curated selection of work spanning web development, design, branding, and software engineering.
        </motion.p>
      </div>

      {/* Search & Filters */}
      <div className="max-w-7xl mx-auto px-6 mb-12">
        {/* Search bar */}
        <div className="relative max-w-md mb-8">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" size={16} />
          <input
            type="text"
            placeholder="Search projects, technologies..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-12 pr-10 py-3 rounded-full font-body text-sm bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-purple-500/50 transition-colors"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
              <FiX size={14} />
            </button>
          )}
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-3">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full font-body text-xs font-medium transition-all duration-200 ${
                activeCategory === cat
                  ? 'text-white border border-purple-500'
                  : 'text-white/50 border border-white/10 hover:border-white/30 hover:text-white/70'
              }`}
              style={activeCategory === cat ? { background: 'linear-gradient(135deg, rgba(127,4,155,0.4), rgba(28,12,25,0.4))' } : {}}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Projects Grid */}
      <div className="max-w-7xl mx-auto px-6">
        {filtered.length > 0 ? (
          <>
            <p className="font-body text-xs text-white/30 mb-8">
              Showing {filtered.length} project{filtered.length !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
            </div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-32"
          >
            <p className="font-display text-4xl text-white/20 mb-4">No projects found</p>
            <p className="font-body text-sm text-white/30">Try a different search term or category</p>
            <button
              onClick={() => { setSearch(''); setActiveCategory('All'); }}
              className="mt-6 btn-outline text-sm"
            >
              Clear Filters
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ProjectsPage;
